import { useEffect, useRef, useState } from 'react'
import {
  Activity,
  CheckSquare2,
  ChevronDown,
  ClipboardCheck,
  GripVertical,
  Heading1,
  Heading2,
  Heading3,
  HelpCircle,
  ImagePlus,
  ListChecks,
  MessageSquareQuote,
  Minus,
  Pill,
  Pilcrow,
  Plus,
  Quote,
  Scale,
  Soup,
  Stethoscope,
  Toilet,
  Trash2,
} from 'lucide-react'
import { createClientId } from '../../core/id'
import type { BlockType, EditorBlock, EditorSurface } from '../../types'

type Option = { type: BlockType; label: string; detail: string; icon: typeof Heading1; surfaces: EditorSurface[] }

const blockOptions: Option[] = [
  { type: 'heading1', label: '제목 1', detail: '페이지의 대표 제목', icon: Heading1, surfaces: ['platform'] },
  { type: 'heading2', label: '제목', detail: '기록이나 구역의 제목', icon: Heading2, surfaces: ['platform', 'member', 'hospital'] },
  { type: 'heading3', label: '소제목', detail: '세부 내용을 나누는 제목', icon: Heading3, surfaces: ['platform', 'member', 'hospital'] },
  { type: 'paragraph', label: '본문', detail: '자유롭게 입력하는 설명', icon: Pilcrow, surfaces: ['platform', 'member', 'hospital'] },
  { type: 'callout', label: '안내', detail: '놓치면 안 되는 안내문', icon: MessageSquareQuote, surfaces: ['platform', 'member', 'hospital'] },
  { type: 'quote', label: '전달받은 설명', detail: '보호소나 병원에서 들은 설명', icon: Quote, surfaces: ['platform', 'member'] },
  { type: 'checklist', label: '확인 항목', detail: '진료 전후 확인할 내용', icon: ListChecks, surfaces: ['platform', 'member', 'hospital'] },
  { type: 'symptom', label: '증상', detail: '시작 시점과 변화 관찰', icon: Activity, surfaces: ['member', 'hospital'] },
  { type: 'measurement', label: '측정값', detail: '체중, 체온, 횟수 등 수치', icon: Scale, surfaces: ['member', 'hospital'] },
  { type: 'meal', label: '식사와 물', detail: '먹은 양과 물 섭취', icon: Soup, surfaces: ['member'] },
  { type: 'stool', label: '배변과 소변', detail: '횟수, 색, 형태, 이상 징후', icon: Toilet, surfaces: ['member'] },
  { type: 'medication', label: '투약과 처치', detail: '약 이름, 용량과 반응', icon: Pill, surfaces: ['member', 'hospital'] },
  { type: 'media', label: '사진과 영상 안내', detail: '함께 첨부할 자료 안내', icon: ImagePlus, surfaces: ['member'] },
  { type: 'question', label: '보호자가 궁금한 점', detail: '걱정되거나 궁금한 내용을 기록', icon: HelpCircle, surfaces: ['member', 'hospital'] },
  { type: 'clinic_note', label: '병원 확인 메모', detail: '의료진이 확인한 사실과 안내', icon: Stethoscope, surfaces: ['hospital'] },
  { type: 'decision', label: '다음 조치', detail: '검사, 관찰, 재내원 계획', icon: ClipboardCheck, surfaces: ['hospital'] },
  { type: 'divider', label: '구분선', detail: '내용 사이를 구분', icon: Minus, surfaces: ['platform', 'member', 'hospital'] },
]

const labels: Partial<Record<BlockType, string>> = {
  symptom: '증상 관찰', measurement: '측정값', meal: '식사와 물', stool: '배변과 소변', medication: '투약과 처치', media: '함께 볼 사진과 영상', question: '보호자가 궁금한 점', clinic_note: '병원 확인 메모', decision: '다음 조치',
}

const placeholders: Record<BlockType, string> = {
  heading1: '대표 제목을 입력하세요.',
  heading2: '제목을 입력하세요.',
  heading3: '소제목을 입력하세요.',
  paragraph: '내용을 입력하거나 /를 눌러 블록을 선택하세요.',
  callout: '중요한 안내 내용을 입력하세요.',
  quote: '전달받은 설명을 가능한 그대로 기록하세요.',
  divider: '',
  checklist: '확인할 항목을 입력하세요.',
  symptom: '언제 시작됐고 평소와 무엇이 다른지 입력하세요.',
  measurement: '예: 체중 2.4kg, 체온 38.2℃, 설사 4회',
  meal: '먹은 종류, 양, 시간과 평소 대비 변화를 입력하세요.',
  stool: '횟수, 색, 형태, 냄새와 피 또는 점액 여부를 입력하세요.',
  medication: '약 이름, 투약 시간, 용량, 성공 여부와 반응을 입력하세요.',
  media: '어떤 장면을 사진이나 영상으로 남길지 입력하세요.',
  question: '병원에 물어보고 싶은 내용을 평소 말투로 적어 주세요.',
  clinic_note: '확인한 사실과 보호자에게 전달할 내용을 입력하세요.',
  decision: '검사, 관찰, 투약, 재내원 등 다음 조치를 입력하세요.',
}

function BlockCommand({ surface, onSelect }: { surface: EditorSurface; onSelect: (type: BlockType) => void }) {
  return <div className="block-command" role="menu"><div className="command-title"><strong>블록 선택</strong><span>필요한 기록 형식을 추가하세요.</span></div>{blockOptions.filter((item) => item.surfaces.includes(surface)).map(({ type, label, detail, icon: Icon }) => <button type="button" role="menuitem" key={type} onClick={() => onSelect(type)}><span><Icon /></span><span><strong>{label}</strong><small>{detail}</small></span></button>)}</div>
}

function EditableBlock({ block, surface, index, onChange, onRemove, onAddAfter, onTypeChange, onCheck, onDragStart, onDragEnter, onDrop, onDragEnd }: {
  block: EditorBlock
  surface: EditorSurface
  index: number
  onChange: (value: string) => void
  onRemove: () => void
  onAddAfter: () => void
  onTypeChange: (type: BlockType) => void
  onCheck: () => void
  onDragStart: () => void
  onDragEnter: () => void
  onDrop: () => void
  onDragEnd: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const editable = useRef<HTMLElement | null>(null)
  useEffect(() => { if (editable.current && editable.current.textContent !== block.content) editable.current.textContent = block.content }, [block.content])
  const Tag = block.type === 'heading1' ? 'h1' : block.type === 'heading2' ? 'h2' : block.type === 'heading3' ? 'h3' : block.type === 'quote' ? 'blockquote' : 'p'
  const insertType = (type: BlockType) => { onTypeChange(type); setMenuOpen(false); window.setTimeout(() => editable.current?.focus(), 0) }
  return <div className={`editor-block type-${block.type}`} data-block-index={index} onDragEnter={(event) => { event.preventDefault(); onDragEnter() }} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); onDrop() }}>
    <div className="block-tools"><button type="button" className="block-add" onClick={() => setMenuOpen((open) => !open)} aria-label="블록 종류 선택" aria-expanded={menuOpen}><Plus /></button><button type="button" className="block-drag" draggable onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; onDragStart() }} onDragEnd={onDragEnd} aria-label="블록 이동"><GripVertical /></button></div>
    {block.type === 'divider' ? <hr /> : <div className="block-content-wrap">{block.type === 'checklist' && <button type="button" className={`editor-checkbox ${block.checked ? 'checked' : ''}`} onClick={onCheck} aria-label={block.checked ? '완료 해제' : '완료 표시'}><CheckSquare2 /></button>}<div className="block-writing">{labels[block.type] && <span className="clinical-block-label">{labels[block.type]}</span>}<Tag ref={(node: HTMLElement | null) => { editable.current = node }} contentEditable suppressContentEditableWarning data-placeholder={placeholders[block.type]} onInput={(event) => { const value = event.currentTarget.textContent || ''; onChange(value); if (value.endsWith('/')) setMenuOpen(true) }} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); onAddAfter() } if (event.key === 'Backspace' && !event.currentTarget.textContent) { event.preventDefault(); onRemove() } }} /></div></div>}
    <button type="button" className="block-delete" onClick={onRemove} aria-label="블록 삭제"><Trash2 /></button>
    {menuOpen && <BlockCommand surface={surface} onSelect={insertType} />}
  </div>
}

export function BlockEditor({ blocks, onChange, surface = 'platform', pageLabel }: { blocks: EditorBlock[]; onChange: (blocks: EditorBlock[]) => void; surface?: EditorSurface; pageLabel?: string }) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const update = (id: string, patch: Partial<EditorBlock>) => onChange(blocks.map((block) => block.id === id ? { ...block, ...patch } : block))
  const addAfter = (index: number, type: BlockType = 'paragraph') => { const next = [...blocks]; next.splice(index + 1, 0, { id: createClientId('block-'), type, content: '' }); onChange(next) }
  const remove = (id: string) => { if (blocks.length === 1) onChange([{ ...blocks[0], content: '' }]); else onChange(blocks.filter((block) => block.id !== id)) }
  const drop = () => {
    if (dragIndex === null || dropIndex === null) return
    const next = [...blocks]
    const [item] = next.splice(dragIndex, 1)
    next.splice(Math.max(0, Math.min(dropIndex, next.length)), 0, item)
    setDragIndex(null); setDropIndex(null); onChange(next)
  }
  const append = (type: BlockType = 'paragraph') => onChange([...blocks, { id: createClientId('block-'), type, content: '' }])
  return <div className={`notion-editor surface-${surface}`}>
    {pageLabel && <div className="editor-page-meta"><span>{pageLabel}</span><ChevronDown size={15} /></div>}
    <div className="editor-page-body" onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); drop() }}>
      {blocks.map((block, index) => <div className="editor-block-slot" key={block.id}>{dropIndex === index && dragIndex !== null && dragIndex > index && <div className="block-drop-ghost"><span />여기에 놓기</div>}<EditableBlock block={block} surface={surface} index={index} onChange={(content) => update(block.id, { content })} onTypeChange={(type) => update(block.id, { type })} onCheck={() => update(block.id, { checked: !block.checked })} onRemove={() => remove(block.id)} onAddAfter={() => addAfter(index)} onDragStart={() => { setDragIndex(index); setDropIndex(index) }} onDragEnter={() => setDropIndex(index)} onDrop={drop} onDragEnd={() => { setDragIndex(null); setDropIndex(null) }} />{dropIndex === index && dragIndex !== null && dragIndex < index && <div className="block-drop-ghost"><span />여기에 놓기</div>}</div>)}
      <div className="editor-end-drop-zone" onDragEnter={(event) => { event.preventDefault(); setDropIndex(blocks.length) }} onDragOver={(event) => event.preventDefault()}>{dropIndex === blocks.length && dragIndex !== null && dragIndex !== blocks.length - 1 && <div className="block-drop-ghost"><span />맨 아래에 놓기</div>}</div>
      <div className="editor-footer-add"><button type="button" onClick={() => append()}><Plus />블록 추가</button><button type="button" onClick={() => append(surface === 'member' ? 'symptom' : surface === 'hospital' ? 'clinic_note' : 'heading2')}>{surface === 'member' ? '증상 블록' : surface === 'hospital' ? '병원 메모' : '제목 블록'}</button></div>
    </div>
  </div>
}
